import React from 'react'
import styled, { useTheme } from 'styled-components'
import { format, parseISO } from 'date-fns'
import patchNote from './patchnote.json'
import SproutIcon from '../icons/IconLib/SproutIcon'
import DownloadIcon from '../icons/IconLib/DownloadIcon'
import WrenchIcon from '../icons/IconLib/WrenchIcon'
import { IconProps } from '../icons/IconLib/model/IconInterface'

type PatchItem = { title: string; description: string }
type SectionKey = 'features' | 'updates' | 'fixes'

const SECTIONS: { key: SectionKey; label: string; Icon: React.FC<IconProps> }[] = [
  { key: 'features', label: 'תכונות חדשות', Icon: SproutIcon },
  { key: 'updates', label: 'שיפורים', Icon: DownloadIcon },
  { key: 'fixes', label: 'תיקונים', Icon: WrenchIcon },
]

const formatDate = (value?: string): string | null => {
  if (!value) return null
  try {
    return format(parseISO(value), 'dd.MM.yyyy')
  } catch {
    return null
  }
}

const PatchNoteView: React.FC = () => {
  const theme = useTheme()
  const tone: Record<SectionKey, string> = {
    features: theme.colors.accentDeep,
    updates: theme.colors.ink2,
    fixes: theme.colors.warning,
  }

  const sections = (patchNote.sections ?? {}) as Record<SectionKey, PatchItem[]>
  const present = SECTIONS.filter((s) => (sections[s.key]?.length ?? 0) > 0)
  const hasChanges = !!patchNote.has_changes && present.length > 0

  const start = formatDate(patchNote.week_start)
  const end = formatDate(patchNote.week_end)
  const dateRange = start && end ? `${start} – ${end}` : start || end

  return (
    <Shell>
      <Header>
        <Eyebrow>
          <Dot />
          עדכוני מערכת
        </Eyebrow>
        {dateRange && <DateRange>{dateRange}</DateRange>}
      </Header>

      {!hasChanges ? (
        <Empty>אין עדכונים חדשים</Empty>
      ) : (
        present.map(({ key, label, Icon }) => (
          <Section key={key}>
            <SectionHead>
              <SectionIcon style={{ color: tone[key] }}>
                <Icon size={18} color={tone[key]} />
              </SectionIcon>
              <SectionLabel>{label}</SectionLabel>
              <Count>{sections[key].length}</Count>
            </SectionHead>
            <Items style={{ borderInlineStartColor: tone[key] }}>
              {sections[key].map((item, idx) => (
                <Item key={idx}>
                  <ItemTitle>{item.title}</ItemTitle>
                  <ItemDesc>{item.description}</ItemDesc>
                </Item>
              ))}
            </Items>
          </Section>
        ))
      )}
    </Shell>
  )
}

export default PatchNoteView

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  direction: rtl;
`

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
`

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.ink3};
`

const Dot = styled.span`
  width: 0.4rem;
  height: 0.4rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.accent};
`

const DateRange = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink2};
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SectionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const SectionLabel = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.heading.h1};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.headingMobile.h1};
  }
`

const Count = styled.span`
  min-width: 1.25rem;
  padding: 0 0.375rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.line2};
  color: ${({ theme }) => theme.colors.ink3};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
`

const Items = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0.875rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  border-inline-start: 2px solid ${({ theme }) => theme.colors.line};
  padding-inline-start: 0.875rem;
  margin-inline-start: 0.5rem;
`

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`

const ItemTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.heading.h3};
  line-height: 1.3;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.headingMobile.h3};
  }
`

const ItemDesc = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.ink2};
`

const Empty = styled.div`
  padding: 2rem 0;
  text-align: center;
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink3};
`
